import { Button, Loader, Modal, Spacer, SvgLink, Text } from '@loomhq/lens';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showErrorBar } from 'actions/errorBar';
import {
  hideCollaborateModal,
  removeFolders,
  removeVideos,
} from 'actions/videosDashboard';
import { ERROR } from 'constants/error-severities';
import { CREATE_PUBLIC_LINK, INVITE_TO_EDIT } from 'constants/events';
import { LOOM_URI } from 'constants/routes';
import { FOLDER, VIDEO } from 'constants/videosDashboard';
import { shareFolder } from 'creators/videosDashboard';
import * as analytics from 'utilities/analytics';
import { getUserAvatarThumb } from 'utilities/avatar';
import { getGraphQLClient } from 'utilities/graphql';
import * as sentry from 'utilities/sentry';

import useClipboard from 'hooks/useClipboard';
import { useGetSelectedWorkspace } from 'hooks/workspace';
import { selectCurrentUser } from 'selectors/user';
import {
  selectCollaborationEntity,
  selectWorkspace,
} from 'selectors/videoDashboard';

import addEditor from './graphql/addEditor.graphql';
import getEditors from './graphql/getCurrentEditors.graphql';
import getMembers from './graphql/getWorkspaceMembers.graphql';
import removeEditor from './graphql/removeEditor.graphql';
import {
  canMarkElementAsComplete,
  completeElementHandler,
} from '../dashboard-onboarding/utilities';
import { WORKSPACE_MEMBER_STATUS_ACTIVE } from '../../../../../shared/constants/workspaceMemberStatus';
import { ADD_COLLABORATOR_CARD_KEY } from '../../../../../shared/constants/workspaceOnboarding';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
} from '../../../../../shared/constants/workspaces';
import { email as validateEmail } from '../../../../../shared/validate';

import SearchTypeahead from 'components/search-typeahead';

import './styles.less';

const Sending = () => {
  return (
    <div className="sendingInvites">
      <div className="message">
        <Text variant="mainTitle">Invites are on the way!</Text>
      </div>
    </div>
  );
};

const CurrentEditorList = ({ editors, removeCurrentEditor, ownerEmail }) => {
  // Make sure the owner is always listed first
  const editorsWithOwner = [
    {
      email: ownerEmail,
      invitePending: false,
      isOwner: true,
    },
  ].concat(editors);

  return (
    <>
      <Spacer top={3} />
      <div className="editorList">
        {editorsWithOwner.map(editor => {
          const isDisabled = editor.isOwner || editor.inheritedPermissions;

          return (
            <div key={editor.email} className="editorRow">
              {editor.email} {editor.invitePending ? '(pending invite)' : ''}
              <a
                href="/"
                className={classnames({
                  disabledLink: isDisabled,
                })}
                onClick={e => {
                  e.preventDefault();

                  if (isDisabled) {
                    return;
                  }

                  removeCurrentEditor(editor.userId);
                }}
              >
                <Text
                  htmlTag="span"
                  fontWeight="bold"
                  color={isDisabled ? 'bodyDimmed' : ''}
                >
                  {editor.isOwner ? 'Owner' : 'Remove'}
                </Text>
              </a>
            </div>
          );
        })}
      </div>
      <Spacer bottom={1} />
    </>
  );
};

const MainButton = ({ sendingInvites, onClick, currentEditors }) => {
  if (sendingInvites) {
    return null;
  }

  const disabled = !currentEditors.some(editor => editor.invitePending);

  return (
    <Button variant="primary" isDisabled={disabled} onClick={onClick}>
      Send Invite
    </Button>
  );
};

const AlternativeButton = ({ sendingInvites, videoUrl }) => {
  const { entityType, entityId, entityName, entityIsShared } = useSelector(
    selectCollaborationEntity
  );
  const dispatch = useDispatch();
  const [isCopied, setCopied] = useClipboard(videoUrl, {
    successDuration: 2000,
  });
  const onClipboard = e => {
    e.preventDefault();
    setCopied();
  };

  if (sendingInvites) {
    return null;
  }

  if (entityType === VIDEO) {
    return (
      <Button icon={<SvgLink />} onClick={onClipboard}>
        {isCopied ? 'Link Copied' : 'Copy Link'}
      </Button>
    );
  }

  return (
    <Button
      icon={<SvgLink />}
      onClick={() => {
        dispatch(hideCollaborateModal());
        dispatch(shareFolder(entityId, entityName, entityIsShared));

        analytics.track(CREATE_PUBLIC_LINK);
      }}
    >
      Copy Link
    </Button>
  );
};

const CollaborateModal = () => {
  const [currentEditors, manageCurrentEditors] = useState([]);
  const [sendingInvites, toggleSendingInvites] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loading, toggleLoading] = useState(false);
  const [removeFromView, setRemoveFromView] = useState(false);

  const {
    entityType,
    entityId,
    entityName,
    entityOwnerEmail,
    entityOwnerId,
  } = useSelector(selectCollaborationEntity);
  const workspace = useSelector(selectWorkspace);
  const { email: currentUserEmail } = useSelector(selectCurrentUser);
  const selectedWorkspace = useGetSelectedWorkspace();
  const selectedWorkspaceId = selectedWorkspace?.id;

  const dispatch = useDispatch();

  const title = `Invite people to edit ${entityName}`;
  const videoUrl = `${LOOM_URI}/share/${entityId}`;
  const modalText =
    workspace === PRIVATE_WORKSPACE
      ? 'Once you invite someone, this item can be found in your Shared library'
      : 'If you remove all collaborators this item will return to your Private library';

  const readersCannotEditText =
    'Others invited to edit who do not have Creator permissions will only be able to ' +
    'view, not edit.';

  const onAddSelection = result => {
    const selectedMember = workspaceMembers.find(
      member => member.email === result.text
    );
    const isAlreadyEditor = currentEditors.find(
      editor => editor.userId === selectedMember.id
    );

    if (isAlreadyEditor) {
      return;
    }

    manageCurrentEditors([
      {
        userId: selectedMember.id,
        email: selectedMember.email,
        inheritedPermissions: false,
        invitePending: true,
      },
      ...currentEditors,
    ]);
  };

  const emailValidation = email => {
    if (email === currentUserEmail) {
      return "You can't invite yourself!";
    }

    if (email === entityOwnerEmail) {
      return 'The owner already has access to edit';
    }

    if (!validateEmail(email)) {
      return "Oops! That doesn't look like a valid email";
    }

    const user = workspaceMembers.find(member => member.email === email);

    if (!user) {
      return 'You can only invite members of this workspace';
    }
  };

  const sendInvites = async () => {
    try {
      toggleSendingInvites(true);

      const client = getGraphQLClient();
      const records = currentEditors
        .filter(editor => editor.invitePending)
        .map(invite => {
          return {
            entityId,
            entityType,
            email: invite.email,
            write: true,
            delete: true,
          };
        });

      const { data } = await client.mutate({
        mutation: addEditor,
        variables: { permissions: [...records] },
      });

      if (data.addSharedPermission.success) {
        const editors = currentEditors.map(editor => {
          return {
            ...editor,
            invitePending: false,
          };
        });

        manageCurrentEditors([...editors]);

        if (!removeFromView) {
          setRemoveFromView(data.addSharedPermission.removeFromView);
        }

        if (
          canMarkElementAsComplete(
            selectedWorkspace.onboarding,
            ADD_COLLABORATOR_CARD_KEY
          )
        ) {
          completeElementHandler(
            dispatch,
            ADD_COLLABORATOR_CARD_KEY,
            selectedWorkspaceId
          );
        }

        records.map(record => {
          const invitee = workspaceMembers.find(
            member => member.email === record.email
          );

          analytics.track(INVITE_TO_EDIT, {
            entity_type: entityType,
            entity_id: entityId,
            entity_owner_id: entityOwnerId,
            invitee_email: record.email,
            invitee_user_id: invitee.id,
            is_owner: currentUserEmail === entityOwnerEmail,
          });
        });
      }

      toggleSendingInvites(false);
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'An error occurred sending invites'));
      sentry.logException(err, 'error sending collaboration invites');
      toggleSendingInvites(false);
    }
  };

  const removeCurrentEditor = async userId => {
    const editor = currentEditors.find(editor => editor.userId === userId);

    if (!editor) {
      return;
    }

    try {
      if (!editor.invitePending) {
        const client = getGraphQLClient();

        await client.mutate({
          mutation: removeEditor,
          variables: { entityId, userId },
        });
      }

      const newEditors = currentEditors.filter(
        editor => editor.userId !== userId
      );

      manageCurrentEditors([...newEditors]);
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'Unable to remove editor'));
      sentry.logException(err, 'error removing editor');
    }
  };

  useEffect(() => {
    if (!selectedWorkspaceId) {
      return;
    }

    toggleLoading(true);
    const client = getGraphQLClient();

    async function getWorkspaceMembers() {
      try {
        const { data } = await client.query({
          query: getMembers,
          variables: {
            workspaceId: selectedWorkspaceId,
            status: WORKSPACE_MEMBER_STATUS_ACTIVE,
          },
          fetchPolicy: 'no-cache',
        });

        const { workspaceMembers } = data;
        // remove the current user and owner from the list first so they can't select themselves
        const members = workspaceMembers.accepted
          .filter(
            member =>
              member.user.email !== currentUserEmail &&
              member.user.email !== entityOwnerEmail
          )
          .map(member => {
            const photo = getUserAvatarThumb(member.user.avatars);

            return {
              photo,
              text: `${member.user.first_name || ''} ${member.user.last_name ||
                ''}`,
              subtext: member.user.email,
              id: member.user.id,
              email: member.user.email,
            };
          });

        setWorkspaceMembers([...members]);
      } catch (err) {
        dispatch(showErrorBar(ERROR, 'Unable to fetch worskpace members'));
        sentry.logException(err, 'error fetching workspace members');
      }
    }

    async function getCurrentEditors() {
      try {
        const client = getGraphQLClient();

        const { data } = await client.query({
          query: getEditors,
          variables: { entityId },
          fetchPolicy: 'no-cache',
        });

        const { sharedEntityAccessList: accessList } = data;

        const editors = accessList.map(editor => {
          return {
            userId: editor.entity_shared_with_user.id,
            email: editor.entity_shared_with_user.email,
            inheritedPermissions: !editor.is_top_level,
            invitePending: false,
          };
        });

        manageCurrentEditors([...editors]);
      } catch (err) {
        dispatch(showErrorBar(ERROR, 'Unable to fetch current editors'));
        sentry.logException(err, 'error fetching current editors');
      }
    }

    async function loadData() {
      await Promise.all([getCurrentEditors(), getWorkspaceMembers()]);

      toggleLoading(false);
    }

    loadData();
  }, [
    dispatch,
    entityId,
    currentUserEmail,
    entityOwnerEmail,
    selectedWorkspaceId,
  ]);

  const closeModal = () => {
    if (
      removeFromView ||
      (!currentEditors.length && workspace === SHARED_WORKSPACE)
    ) {
      if (entityType === FOLDER) {
        dispatch(removeFolders([entityId]));
      } else {
        dispatch(removeVideos([entityId]));
      }
    }

    dispatch(hideCollaborateModal());
  };

  return (
    <Modal
      title={sendingInvites ? '' : title}
      isOpen={true}
      onCloseClick={() => closeModal(dispatch)}
      mainButton={
        <MainButton
          sendingInvites={sendingInvites}
          onClick={sendInvites}
          currentEditors={currentEditors}
        />
      }
      alternativeButton={
        <AlternativeButton
          sendingInvites={sendingInvites}
          videoUrl={videoUrl}
        />
      }
    >
      <>
        {loading && (
          <div className="loader">
            <Loader size="large" />
          </div>
        )}

        {sendingInvites && <Sending />}

        {!sendingInvites && !loading && (
          <>
            <div className="modalBodyText">
              <p>{modalText}</p>
              <Spacer bottom={2} />
              <p>{readersCannotEditText}</p>
            </div>
            <SearchTypeahead
              ctaButtonText="Add to list"
              returnInputOnEmptySelection={true}
              queryValidation={emailValidation}
              suggestions={workspaceMembers}
              onSelection={onAddSelection}
            />

            <CurrentEditorList
              editors={currentEditors}
              removeCurrentEditor={removeCurrentEditor}
              ownerEmail={entityOwnerEmail}
            />
          </>
        )}
      </>
    </Modal>
  );
};

export default CollaborateModal;
